import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { TailSpin } from "react-loader-spinner";
import ShareToQuickBooks from "./ShareToQuickBooks";
import ComplianceCheck from "./ComplianceCheck";
import ShareToWave from "./ShareToWave";
import ExpensePolicyStatus from "./ExpensePolicyStatus";
import ComplianceDocumentsModal from "./ComplianceDocumentsModal";
import { useCompliance } from "./ComplianceContext";
import { toast } from "react-toastify";
// import AcctViewerHelpTooltip from "./utils/AccountantViewTooltip";
//import CommonHelpTooltip from "./utils/CommonHelpTooltip";
import VideoHelpModal from "./utils/VideoHelpModal";
import ApprovalConfirmationModal from "./ApprovalConfirmationModal";
import { getEffectiveSpreadsheetId } from "./utils/reportUtils";
import CommentsSummary from "./CommentsSummary";
import "../styles/ProcessInfo.css";

const AccountantView = ({ clientEmail }) => {
  const [showVideoHelp, setShowVideoHelp] = useState(false);
  const [showExpensePolicyTutorial, setShowExpensePolicyTutorial] =
    useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);
  const { hasComplianceDocuments } = useCompliance();
  const [downloadingReportId, setDownloadingReportId] = useState(null);
  const [downloadingFormat, setDownloadingFormat] = useState(null);

  const [showComplianceDocs, setShowComplianceDocs] = useState(false);
  const [isApprovalLoading, setIsApprovalLoading] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [markingPaidReportId, setMarkingPaidReportId] = useState(null);

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const highlightedReportId = searchParams.get("reportId");
  const highlightedReportRef = useRef(null);
  const [investmentManager, setInvestmentManager] = useState(
    location.state?.investmentManager || true
  );
  const currentUserEmail = location.state?.currentUserEmail;
  const downloadRefs = useRef({}); // Add ref for download dropdown menus

  // Grab the roles or just the boolean

  const [accountantData, setAccountantData] = useState(null);
  const [reports, setReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showCommentsPanel, setShowCommentsPanel] = useState(false);

  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingAccountantView, setIsLoadingAccountantView] = useState(true);
  const [quickbooksConnectionStatus, setQuickbooksConnectionStatus] =
    useState(false);
  const [reportFilter, setReportFilter] = useState("All");
  const [editingReportId, setEditingReportId] = useState(null);
  const [editedNote, setEditedNote] = useState("");
  const navigate = useNavigate();

  const [waveConnectionStatus, setWaveConnectionStatus] = useState(false);
  const SHOW_WAVE_BUTTON = false; // Set to `true` if you want it visible

  const textareaRef = useRef(null);

  // Add click-away listener for the download menu
  useEffect(() => {
    function handleClickOutside(event) {
      // If no dropdown is open, do nothing
      if (!downloadingReportId) return;

      // Get the ref for the currently open dropdown
      const currentDropdownRef = downloadRefs.current[downloadingReportId];

      // If we do have a ref and the click is outside it, close the menu
      if (currentDropdownRef && !currentDropdownRef.contains(event.target)) {
        setDownloadingReportId(null);
      }
    }

    // Listen for all mousedown events on the document
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [downloadingReportId]);

  // Add this effect to scroll to the highlighted report
  useEffect(() => {
    if (highlightedReportId && highlightedReportRef.current) {
      highlightedReportRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      // Add a highlight effect
      highlightedReportRef.current.classList.add("highlighted-report");

      // Remove the highlight effect after a delay
      const timer = setTimeout(() => {
        if (highlightedReportRef.current) {
          highlightedReportRef.current.classList.remove("highlighted-report");
        }
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [highlightedReportId, reports]);

  // In AccountantView.jsx, add a useEffect to handle loading state:

  useEffect(() => {
    // Clear any lingering redirectLock if it somehow persisted
    localStorage.removeItem("redirectLock");

    // Signal that we've successfully loaded the AccountantView
    sessionStorage.setItem("accountantViewLoaded", "true");

    return () => {
      // Clean up when component unmounts
      sessionStorage.removeItem("accountantViewLoaded");
    };
  }, []);

  useEffect(() => {
    const checkWaveConnection = async () => {
      if (reports.length > 0) {
        try {
          const waveResponse = await fetch("/api/wave/connection-status", {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });

          if (waveResponse.ok) {
            const status = await waveResponse.json();
            setWaveConnectionStatus(status?.connected || false);
          }
        } catch (error) {
          console.error("Error checking Wave connection:", error);
          setWaveConnectionStatus(false);
        }
      }
    };

    checkWaveConnection();
  }, [reports]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setEditingReportId(null);
      }
    };

    if (editingReportId) {
      window.addEventListener("keydown", handleEsc);
      return () => window.removeEventListener("keydown", handleEsc);
    }
  }, [editingReportId]);

  useEffect(() => {
    let isMounted = true;
    const fetchAccountantData = async () => {
      if (!clientEmail) {
        console.warn("⚠️ No client email provided. Skipping data fetch.");
        setReports([]);
        setAccountantData(null);
        return;
      }

      // Add magic link redirect handling
      const isMagicLinkAuth =
        localStorage.getItem("auth_provider") === "magic-link";
      const isFirstLoad =
        sessionStorage.getItem("accountantview_first_load") !== "true";

      if (isMagicLinkAuth && isFirstLoad) {
        // Set the flag to prevent this check on subsequent renders
        sessionStorage.setItem("accountantview_first_load", "true");

        // Clear any redirect flags that might be causing loops
        sessionStorage.removeItem("magic_link_redirect_complete");
        sessionStorage.removeItem("magic_link_redirect_count");

        // Short delay to ensure everything is ready before fetching
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      try {
        setIsLoadingAccountantView(true);

        console.log("📌 Fetching data for client:", clientEmail);
        const response = await axios.get(
          `/api/client-reports/${clientEmail}?page=${currentPage}&limit=50&filter=${reportFilter}${
            highlightedReportId ? `&reportId=${highlightedReportId}` : ""
          }`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        console.log("Response received:", response.data);

        if (!isMounted) return;

        if (response.data.success) {
          // Set basic client info
          setAccountantData(response.data.processInfo);

          // Extract investmentManager flag from the response
          // This assumes your API returns this information, you may need to adjust
          // the property path based on your actual API response structure
          const hasInvestmentManager =
            response.data.investmentManager ||
            response.data.processInfo?.investmentManager ||
            false;

          console.log(
            "Setting investmentManager flag to:",
            hasInvestmentManager
          );
          setInvestmentManager(hasInvestmentManager);

          // Set reports from the response
          console.log("Setting reports:", response.data.reports);
          setReports(response.data.reports || []);
          setInvestmentManager(response.data.investmentManager || false);

          // Set pagination data if available
          if (response.data.pagination) {
            setTotalPages(response.data.pagination.totalPages);
          }

          // Check QuickBooks connection if there are reports
          if (response.data.reports?.length > 0) {
            const qbResponse = await fetch("/api/quickbooks/status", {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            });

            if (qbResponse.ok && isMounted) {
              const status = await qbResponse.json();
              setQuickbooksConnectionStatus(status?.connected || false);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching accountant data:", error);
      } finally {
        if (isMounted) {
          setIsLoadingAccountantView(false);
        }
      }
    };

    fetchAccountantData();

    return () => {
      isMounted = false;
      // Cleanup session flag on component unmount if needed
      // sessionStorage.removeItem('accountantview_first_load');
    };
  }, [
    clientEmail,
    currentPage,
    reportFilter,
    highlightedReportId,
    hasComplianceDocuments,
  ]);
  const fetchCommentCounts = useCallback(
    async (reportsToCheck) => {
      if (!reportsToCheck || reportsToCheck.length === 0) return reportsToCheck;

      try {
        const countPromises = reportsToCheck.map((report) => {
          return axios
            .get(
              `/api/comments/${
                report.reportId
              }/count?clientEmail=${encodeURIComponent(clientEmail)}`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              }
            )
            .then((response) => {
              if (response.data.success) {
                return {
                  reportId: report.reportId,
                  count: response.data.counts.unresolved,
                };
              }
              return { reportId: report.reportId, count: 0 };
            })
            .catch((error) => {
              console.error(
                `Error fetching comment count for ${report.reportId}:`,
                error
              );
              return { reportId: report.reportId, count: 0 };
            });
        });

        const results = await Promise.all(countPromises);

        const countsObject = results.reduce(
          (acc, item) => {
            acc[item.reportId] = item.count;
            return acc;
          },
          [clientEmail]
        );

        // Update reports with comment counts
        const updatedReports = reportsToCheck.map((report) => ({
          ...report,
          commentCount: countsObject[report.reportId] || 0,
        }));

        return updatedReports;
      } catch (error) {
        console.error("Error fetching comment counts:", error);
        return reportsToCheck;
      }
    },
    [clientEmail]
  );

  useEffect(() => {
    const updateCommentCounts = async () => {
      if (reports.length > 0) {
        const updatedReports = await fetchCommentCounts(reports);
        setReports(updatedReports);
      }
    };

    updateCommentCounts();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports.length, fetchCommentCounts]);

  const handleDownload = async (reportId, format) => {
    try {
      // Set the downloading format to show loading state
      setDownloadingFormat(format);

      // IMPORTANT: Always use the original reportId, not the calculated effectiveId
      // Include the clientEmail in the URL for accountant view
      const response = await axios({
        url: `/api/downloadReport/${reportId}/${format}?clientEmail=${encodeURIComponent(
          clientEmail
        )}`,
        method: "GET",
        responseType: "blob", // Important for handling file downloads
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));

      // Extract filename from Content-Disposition header if present
      let filename = `expense-report.${format}`;
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      // Create a temporary link and trigger download
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Show success toast if toast is available
      // Show success toast if toast is available
      if (typeof toast !== "undefined") {
        toast.success(
          `Report downloaded successfully as ${format.toUpperCase()}`
        );
      } else {
        console.log(
          `Report downloaded successfully as ${format.toUpperCase()}`
        );
      }

      // Close the download menu after successful download
      setDownloadingReportId(null);
    } catch (error) {
      console.error("Error downloading report:", error);

      // Display error message
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An error occurred while downloading the report";

      // Use toast if available, otherwise use alert
      if (typeof toast !== "undefined") {
        toast.error(`Download failed: ${errorMessage}`);
      } else {
        alert(`Download failed: ${errorMessage}`);
      }
    } finally {
      // Clear downloading format so buttons are re-enabled
      setDownloadingFormat(null);
    }
  };

  // Updated status update handler
  // Modified handleStatusUpdate function for accountantview.js
  const handleStatusUpdate = async (reportId, newStatus) => {
    try {
      // Find the current report to get its title and current status
      const currentReport = reports.find((r) => r.reportId === reportId);

      // If report is already locked and trying to modify it (unless going to Approved again)
      /* ──────────────────────────────────────────────
   Allow exactly one locked transition:
   Approved  ⇒  Paid
 ────────────────────────────────────────────── */
      const isMarkingPaid =
        currentReport.status === "Approved" && newStatus === "Paid";

      if (
        (currentReport.reportMetadata?.isLocked ||
          currentReport.status === "Approved") && // report is locked
        !isMarkingPaid && // but NOT our special case
        newStatus !== "Approved" // and not re-approving
      ) {
        toast.error("This report is locked and cannot be modified");
        return;
      }

      // If status is changing to "Approved", show confirmation modal first
      if (newStatus === "Approved" && currentReport.status !== "Approved") {
        setPendingApproval({
          reportId,
          newStatus,
          title: currentReport.title || "Untitled Report",
        });
        setShowApprovalModal(true);
        return; // Exit early - will continue after confirmation
      }

      // Continue with normal update for non-approval changes
      await updateReportStatus(reportId, newStatus);
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status. Please try again.");
    }
  };

  // Add new function for the actual update
  const updateReportStatus = async (reportId, newStatus) => {
    try {
      console.log("Attempting update with:", {
        clientEmail,
        reportId,
        newStatus,
      });

      // Get the current report to access its metadata
      const currentReport = reports.find((r) => r.reportId === reportId);

      // Add locking metadata if approving
      const updatedMetadata = { ...(currentReport?.reportMetadata || {}) };
      if (newStatus === "Approved") {
        updatedMetadata.isLocked = true;
        updatedMetadata.approvedAt = new Date().toISOString();
        updatedMetadata.approvedBy = currentUserEmail;
      } else if (newStatus === "Paid") {
        // Marking an already-approved report as Paid
        updatedMetadata.paidAt = new Date().toISOString();
        updatedMetadata.paidBy = currentUserEmail;
      }

      const response = await axios.post(
        "/api/accountant/update-client-report",
        {
          reportId,
          status: newStatus,
          clientEmail: clientEmail,
          reportMetadata: updatedMetadata,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.message === "Report updated successfully") {
        setReports((prevReports) =>
          prevReports.map((r) =>
            r.reportId === reportId
              ? {
                  ...r,
                  status: newStatus,
                  reportMetadata: updatedMetadata,
                }
              : r
          )
        );

        // Show success message for approval
        if (newStatus === "Approved") {
          toast.success("Report approved and locked successfully");
        }
      }
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status. Please try again.");
    }
  };

  // ───────────────────────────────────────────────────────────────────────────────
  // Completely rewritten handleApprovalConfirmed()             ← AccountantView.js
  // Copy‑and‑paste this over the existing definition
  // ───────────────────────────────────────────────────────────────────────────────
  const handleApprovalConfirmed = () => {
    console.log("handleApprovalConfirmed called with:", pendingApproval);
    setIsApprovalLoading(true); // Set loading state to true when starting

    if (!pendingApproval) return;

    /* 1️⃣ grab the report we're approving */
    const report = reports.find((r) => r.reportId === pendingApproval.reportId);

    if (!report) {
      toast.error("Could not locate the report in local state.");
      setShowApprovalModal(false);
      setPendingApproval(null);
      setIsApprovalLoading(false); // Reset loading state on error
      return;
    }

    /* 2️⃣ decide which sheet to protect
        ‑ we **only** want the sub‑report's sheet (report.id)
        ‑ as a fallback we'll try to parse it from the report link */
    /* 2️⃣ decide which sheet to protect 
        ‑ we **only** want the sub‑report's sheet (report.id)
        ‑ as a fallback we'll try to parse it from the report link */
    const sheetId = getEffectiveSpreadsheetId(
      report,
      (report.link && report.link.match(/\/d\/([a-zA-Z0-9-_]+)/)?.[1]) || null
    );

    const finishUp = () => {
      setShowApprovalModal(false);
      setPendingApproval(null);
      setIsApprovalLoading(false); // Reset loading state when done
    };

    /* 3️⃣ first update the report's status/metadata in Firestore */
    updateReportStatus(pendingApproval.reportId, pendingApproval.newStatus)
      .then(() => {
        /* 4️⃣ if we can't identify a sheet, we're done */
        if (!sheetId) {
          console.log("No sub‑report sheetId found; skipping protection step.");
          toast.success("Report approved successfully");
          finishUp();
          return;
        }

        /* 5️⃣ lock the sub‑report sheet */
        toast.info("Applying report protection…");
        console.log("Protect call → sheetId:", sheetId);

        return axios
          .post(
            "/api/protect-spreadsheet",
            {
              spreadsheetId: sheetId, // 👈 ONLY the sub‑report sheet
              clientEmail, // ⚠ do NOT change – owner's email
              reportId: pendingApproval.reportId, // echoed for logs
              approverEmail: currentUserEmail, // viewer / approver
              reportTitle: report.title || "Expense Report", // Add report title
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          )
          .then((res) => {
            if (res.data.success) {
              // Extract the locked spreadsheet information
              const { lockedSpreadsheetId, lockedSpreadsheetUrl } = res.data;

              console.log("Got locked spreadsheet:", {
                lockedSpreadsheetId,
                lockedSpreadsheetUrl,
              });

              // Update the reports array with the new URL
              setReports((prevReports) =>
                prevReports.map((r) => {
                  if (r.reportId === pendingApproval.reportId) {
                    return {
                      ...r,
                      link: lockedSpreadsheetUrl, // Update the link directly
                      status: "Approved",
                      reportMetadata: {
                        ...(r.reportMetadata || {}),
                        isLocked: true,
                        approvedBy: currentUserEmail,
                        approvedAt: new Date().toISOString(),
                        lockedSpreadsheetId: lockedSpreadsheetId,
                        lockedSpreadsheetUrl: lockedSpreadsheetUrl, // Store URL in metadata too
                        originalSpreadsheetId: sheetId,
                      },
                    };
                  }
                  return r;
                })
              );

              toast.success("Report approved and spreadsheet protected 🎉");
            } else {
              toast.warning(
                "Report approved, but spreadsheet protection failed."
              );
            }
          })
          .catch((err) => {
            console.error("Protection API error:", err.response || err);
            toast.warning(
              "Report approved, but spreadsheet protection failed."
            );
          })
          .finally(finishUp);
      })
      .catch((err) => {
        console.error("Error during report approval:", err);
        toast.error("Failed to approve report. Please try again.");
        finishUp();
      });
  };

  const handleBack = () => {
    const isMagicLinkUser =
      localStorage.getItem("auth_provider") === "magic-link";

    if (isMagicLinkUser) {
      // For magic link users, navigate to a fixed route without going through history
      // This avoids any session confusion by using a direct navigation target
      navigate("/process-info", {
        state: { selectedMenu: "adminView", returnPage: "adminView" },
        replace: true, // Use replace to prevent back-button issues
      });
      return;
    }

    if (location.state && location.state.from) {
      navigate(location.state.from);
    } else if (location.state && location.state.returnPage === "adminView") {
      navigate("/admin");
    } else if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/process-info");
    }
  };

  const handleManagePolicies = () => {
    // Instead of navigate(...), just open the modal
    setShowComplianceDocs(true);
  };

  const handleNoteUpdate = async (reportId, newNote) => {
    try {
      setIsSavingNote(true);

      // Find the current report to get original note and shared_with list
      const currentReport = reports.find((r) => r.reportId === reportId); // Fix: Use r.reportId instead of r.id
      const originalNote = currentReport?.notes || "";
      const sharedWith = currentReport?.shared_with || [];

      // Only track as change if note is actually different
      const isChange = originalNote !== newNote;

      const response = await axios.post(
        "/api/accountant/update-client-report",
        {
          reportId,
          notes: newNote,
          clientEmail: clientEmail,
          isNoteChange: isChange,
          notifyRecipients: true,
          sharedWith: sharedWith,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.message === "Report updated successfully") {
        setReports(
          reports.map((report) =>
            report.reportId === reportId
              ? { ...report, notes: newNote }
              : report
          )
        );

        // Show toast notification
        if (isChange && response.data.notificationsCount > 0) {
          toast.success(
            `Note updated. ${response.data.notificationsCount} recipients notified.`
          );
        } else if (isChange) {
          toast.success("Note updated successfully.");
        }

        setEditingReportId(null);
      }
    } catch (error) {
      console.error("Failed to save note:", error);
      toast.error("Failed to save note. Please try again.");
    } finally {
      setIsSavingNote(false);
    }
  };

  const formatCurrency = (amount, currency) => {
    if (!currency) return amount.toLocaleString();

    const currencyMap = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      AUD: "A$",
      CAD: "C$",
      CHF: "Fr",
      CNY: "¥",
    };

    const symbol = currencyMap[currency] || currency;
    const formattedAmount = amount.toLocaleString();
    return currencyMap[currency]
      ? `${symbol}${formattedAmount}`
      : `${formattedAmount} ${symbol}`;
  };

  const renderNotesModal = () => {
    if (!editingReportId) return null;

    const currentReport = reports.find((r) => r.reportId === editingReportId);

    // Get current user's name (from email or full name)
    const currentUserName = currentUserEmail
      ? currentUserEmail.split("@")[0]
      : "Accountant";

    // Helper to add your name at cursor position
    const addMyName = () => {
      if (!textareaRef.current) return;

      const textarea = textareaRef.current;
      const cursorPos = textarea.selectionStart;
      const textBefore = editedNote.substring(0, cursorPos);
      const textAfter = editedNote.substring(cursorPos);

      // If cursor is at the very beginning or preceded by a newline, don't add another newline
      const needsNewlineBefore =
        textBefore.length > 0 &&
        !textBefore.endsWith("\n") &&
        !textBefore.endsWith("\n\n");

      // If we need to add newlines, add them appropriately
      const prefixText = needsNewlineBefore ? "\n\n" : "";

      // Insert name with colon
      const nameText = `${currentUserName}: `;

      // Combine everything
      const newText = textBefore + prefixText + nameText + textAfter;
      setEditedNote(newText);

      // Focus and set cursor position right after the inserted name
      setTimeout(() => {
        textarea.focus();
        const newCursorPos = cursorPos + prefixText.length + nameText.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    };

    return (
      <div
        className="eb-modal-overlay"
        onClick={() => setEditingReportId(null)}
      >
        <div className="eb-notes-modal" onClick={(e) => e.stopPropagation()}>
          <div className="eb-notes-content">
            <h4>Report Notes</h4>

            {/* Display the last editor information */}
            {currentReport?.lastModifiedBy && (
              <div className="last-modified-info">
                Last edited by{" "}
                <span className="editor-name">
                  {currentReport.lastModifiedBy.email.split("@")[0]}
                </span>{" "}
                on{" "}
                {new Date(
                  currentReport.lastModifiedBy.timestamp
                ).toLocaleString()}
              </div>
            )}

            <div className="textarea-container">
              <textarea
                ref={textareaRef}
                value={editedNote}
                onChange={(e) => setEditedNote(e.target.value)}
                placeholder="Add notes about this report..."
                className="eb-notes-textarea"
                autoFocus
                disabled={isSavingNote}
              />
              <button
                className="add-name-button"
                onClick={addMyName}
                disabled={isSavingNote}
                title="Add your name at cursor position"
              >
                <i className="fas fa-signature"></i>
              </button>
            </div>

            <div className="eb-modal-actions">
              <button
                className="eb-modal-save"
                onClick={() => handleNoteUpdate(editingReportId, editedNote)}
                disabled={isSavingNote}
              >
                {isSavingNote ? (
                  <span className="save-loading">
                    <TailSpin
                      color="#ffffff"
                      height={14}
                      width={14}
                      style={{ marginRight: "6px" }}
                    />
                    Saving...
                  </span>
                ) : (
                  "Save"
                )}
              </button>
              <button
                className="eb-modal-cancel"
                onClick={() => setEditingReportId(null)}
                disabled={isSavingNote}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderReports = () => (
    <div className="reports-display-section">
      <div className="reports-header">
        <div className="reports-title-section">
          <h3 className="expense-report-title">
            Reports Shared With You{" "}
            <i
              className="fas fa-question-circle"
              style={{ cursor: "pointer", color: "#0066ff", marginLeft: "8px" }}
              onClick={() => setShowVideoHelp(true)}
            ></i>
          </h3>
        </div>
        <div
          className="filter-controls"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Left group: ExpensePolicyStatus and the tutorial icon */}
          <div style={{ display: "flex", alignItems: "center" }}>
            <ExpensePolicyStatus
              clientEmail={currentUserEmail}
              investmentManager={investmentManager}
              onManagePolicies={handleManagePolicies}
            />
            <i
              className="fas fa-question-circle expense-policy-tutorial-icon"
              style={{ marginLeft: "8px" }}
              title="How to check against your Expense Policy"
              onClick={() => setShowExpensePolicyTutorial(true)}
            ></i>
          </div>
          {/* Right group: the reports filter select */}
          <select
            value={reportFilter}
            onChange={(e) => {
              setReportFilter(e.target.value);
              setCurrentPage(1); // reset paginator on every new filter
            }}
            className="status-select"
          >
            <option value="All">All Reports</option>
            <option value="Submitted">Submitted</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Paid">Paid</option>
          </select>
        </div>
      </div>

      <div className="reports-content">
        {reports.length > 0 ? (
          <div>
            {reports
              .filter(
                (r) => reportFilter === "All" || r.status === reportFilter
              )
              .map((report, index) => {
                if (!report.submissionDate || !report.link) return null;

                return (
                  <div
                    key={report.reportId || index}
                    className={`report-card ${
                      highlightedReportId === report.reportId
                        ? "highlighted-report"
                        : ""
                    }`}
                    ref={
                      highlightedReportId === report.reportId
                        ? highlightedReportRef
                        : null
                    }
                  >
                    {/* Header with date, title, and controls */}
                    <div className="report-header">
                      <div className="report-meta">
                        <div className="date-row">
                          <div className="date-block">
                            {new Date(
                              report.submissionDate
                            ).toLocaleDateString()}
                          </div>
                          {/* Add report type badge */}
                          {report.reportMetadata?.reportType && (
                            <div
                              className={`report-type-badge ${report.reportMetadata.reportType}`}
                            >
                              <i
                                className={`fas ${
                                  report.reportMetadata.reportType ===
                                  "receipt_report"
                                    ? "fa-receipt"
                                    : "fa-file-invoice"
                                }`}
                              ></i>
                              <span>
                                {report.reportMetadata.reportType ===
                                "receipt_report"
                                  ? "Receipts"
                                  : "Invoices"}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Desktop title */}
                        <div className="title-left">
                          <a
                            href={
                              report.reportMetadata?.isLocked &&
                              report.reportMetadata?.lockedSpreadsheetUrl
                                ? report.reportMetadata.lockedSpreadsheetUrl
                                : report.link
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {report.title || "Untitled Report"}
                          </a>
                        </div>
                        {/* Mobile title - shown only on mobile */}
                        <div className="mobile-title">
                          <a
                            href={
                              report.reportMetadata?.isLocked &&
                              report.reportMetadata?.lockedSpreadsheetUrl
                                ? report.reportMetadata.lockedSpreadsheetUrl
                                : report.link
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {report.title || "Untitled Report"}
                          </a>
                        </div>
                      </div>

                      <div className="controls-right">
                        {report.status === "Paid" ? (
                          /* ─── ① already Paid → banner only ───────────────────── */
                          <div className="status-paid-locked">
                            <span className="status-paid-text">Paid</span>
                            <i
                              className="fas fa-lock status-lock-icon"
                              title="This report is locked"
                            />
                          </div>
                        ) : report.status === "Approved" ||
                          report.reportMetadata?.isLocked ? (
                          /* ─── ② Approved (locked) → banner + “Mark as Paid” btn ─ */
                          <>
                            <div className="status-approved-locked">
                              <span className="status-approved-text">
                                Approved
                              </span>
                              <i
                                className="fas fa-lock status-lock-icon"
                                title="This report is locked"
                              />
                            </div>

                            {/* single allowed transition while locked: Approved → Paid */}
                            <button
                              className="action-button mark-paid-btn"
                              onClick={() => {
                                if (
                                  window.confirm("Mark this report as paid?")
                                ) {
                                  setMarkingPaidReportId(report.reportId);
                                  handleStatusUpdate(
                                    report.reportId,
                                    "Paid"
                                  ).finally(() => {
                                    setMarkingPaidReportId(null);
                                  });
                                }
                              }}
                              disabled={markingPaidReportId === report.reportId}
                              title="Mark as Paid"
                            >
                              {markingPaidReportId === report.reportId ? (
                                <span className="mark-paid-loading">
                                  <TailSpin
                                    color="#ffffff"
                                    height={16}
                                    width={16}
                                  />
                                </span>
                              ) : (
                                <span className="mark-paid-text">
                                  Mark Paid
                                </span>
                              )}
                            </button>
                          </>
                        ) : (
                          /* ─── ③ open report → editable dropdown ──────────────── */
                          <select
                            value={report.status || "Submitted"}
                            onChange={(e) =>
                              handleStatusUpdate(
                                report.reportId,
                                e.target.value
                              )
                            }
                            className="status-select"
                            title={report.status || "Submitted"}
                          >
                            <option value="Submitted">Submitted</option>
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>

                            {/* disable until the report **is** Approved */}
                            <option
                              value="Paid"
                              disabled={report.status !== "Approved"}
                              style={
                                report.status !== "Approved"
                                  ? { cursor: "not-allowed", opacity: 0.4 }
                                  : {}
                              }
                            >
                              Paid
                            </option>
                          </select>
                        )}

                        <button
                          className="action-button"
                          onClick={() => {
                            if (editingReportId === report.reportId) {
                              setEditingReportId(null);
                            } else {
                              setEditingReportId(report.reportId);
                              setEditedNote(report.notes || "");
                            }
                          }}
                          title={report.notes ? "Edit note" : "Add note"}
                        >
                          <i
                            className={`fas fa-sticky-note ${
                              report.notes && report.notes.trim() !== ""
                                ? "has-content"
                                : ""
                            }`}
                          ></i>
                          {report.notes && report.notes.trim() !== "" && (
                            <span className="note-indicator"></span>
                          )}
                        </button>
                        <button
                          className="action-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCommentsPanel(report.reportId);
                          }}
                          title="View sheet comments"
                        >
                          <div className="comment-icon-container">
                            <i className="fas fa-comment-alt"></i>
                            {report.commentCount > 0 && (
                              <span className="comment-count-badge">
                                {report.commentCount}
                              </span>
                            )}
                          </div>
                        </button>
                        {/* Download dropdown button - Updated with ref */}
                        <div
                          className="download-dropdown"
                          ref={(el) =>
                            (downloadRefs.current[report.reportId] = el)
                          }
                        >
                          <button
                            className="action-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDownloadingReportId(
                                downloadingReportId === report.reportId
                                  ? null
                                  : report.reportId
                              );
                            }}
                            title="Download report"
                          >
                            <i className="fas fa-download"></i>
                          </button>

                          {downloadingReportId === report.reportId && (
                            <div className="download-menu">
                              {["csv", "xlsx", "pdf"].map((fmt) => (
                                <button
                                  key={fmt}
                                  onClick={() => {
                                    if (!downloadingFormat) {
                                      handleDownload(report.reportId, fmt);
                                    }
                                  }}
                                  disabled={downloadingFormat !== null}
                                  className={
                                    downloadingFormat === fmt
                                      ? "downloading"
                                      : ""
                                  }
                                >
                                  {downloadingFormat === fmt ? (
                                    <span className="downloading-indicator">
                                      <TailSpin
                                        color="#0066ff"
                                        height={12}
                                        width={12}
                                        style={{ marginRight: "6px" }}
                                      />
                                      ...
                                    </span>
                                  ) : fmt === "xlsx" ? (
                                    "Excel"
                                  ) : (
                                    fmt.toUpperCase()
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row3: Receipts & Func Total on the same line */}
                    <div className="row3">
                      <div className="metric-left">
                        <span className="label">Receipts:</span>
                        <span className="value">
                          {report.receiptCount || 0}
                        </span>
                      </div>

                      {report.functionalTotal > 0 && (
                        <div className="metric-right">
                          <span className="label">Total:</span>
                          <span className="value currency">
                            {" "}
                            {report.homeCurrency
                              ? formatCurrency(
                                  report.functionalTotal || 0,
                                  report.homeCurrency
                                )
                              : (report.functionalTotal || 0).toLocaleString(
                                  undefined,
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Row4: QuickBooks, Wave, Compliance */}
                    <div className="row4">
                      <div className="accounting-controls">
                        <div className="integrations-buttons-container">
                          <div className="integration-button">
                            <ShareToQuickBooks
                              reportId={report.reportId}
                              spreadsheetId={accountantData.spreadsheetId}
                              quickbooks={report.quickbooks}
                              isConnected={quickbooksConnectionStatus}
                              reportMetadata={report.reportMetadata}
                              onStatusUpdate={(newStatus) => {
                                console.log(
                                  "Updating report with new status:",
                                  newStatus
                                );
                                setReports((prevReports) =>
                                  prevReports.map((r) =>
                                    r.reportId === report.reportId
                                      ? {
                                          ...r,
                                          quickbooks: {
                                            ...r.quickbooks,
                                            ...newStatus,
                                          },
                                          functionalTotal:
                                            newStatus.functionalTotal,
                                        }
                                      : r
                                  )
                                );
                              }}
                              onConnectionStatusChange={
                                setQuickbooksConnectionStatus
                              }
                              isClientOfAccountant={true}
                              clientEmail={clientEmail}
                            />
                          </div>

                          {SHOW_WAVE_BUTTON && (
                            <div className="integration-button">
                              <ShareToWave
                                reportId={getEffectiveSpreadsheetId(
                                  report,
                                  report.reportId
                                )}
                                spreadsheetId={accountantData.spreadsheetId}
                                wave={report.wave}
                                isConnected={waveConnectionStatus}
                                onStatusUpdate={(newStatus) => {
                                  setReports((prevReports) =>
                                    prevReports.map((r) =>
                                      r.reportId === report.reportId
                                        ? {
                                            ...r,
                                            wave: { ...r.wave, ...newStatus },
                                            functionalTotal:
                                              newStatus.functionalTotal,
                                          }
                                        : r
                                    )
                                  );
                                }}
                                onConnectionStatusChange={
                                  setWaveConnectionStatus
                                }
                                isClientOfAccountant={true}
                                clientEmail={clientEmail}
                              />
                            </div>
                          )}

                          {investmentManager && (
                            <div className="integration-button">
                              <ComplianceCheck
                                report={{
                                  ...report,
                                  id: getEffectiveSpreadsheetId(
                                    report,
                                    report.reportId
                                  ), // Ensure correct ID is passed
                                }}
                                clientEmail={clientEmail}
                                spreadsheetId={accountantData.spreadsheetId}
                                onStatusUpdate={(complianceData) => {
                                  setReports((prevReports) =>
                                    prevReports.map((r) =>
                                      r.reportId === report.reportId
                                        ? { ...r, compliance: complianceData }
                                        : r
                                    )
                                  );
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="empty-state">
            <p className="empty-state-message">
              No previously submitted reports available.
            </p>
          </div>
        )}

        {reports.length > 0 && totalPages > 1 && (
          <div className="custom-pagination-controls">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1 || isLoadingAccountantView}
              className="custom-pagination-button"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <span className="custom-pagination-text">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={currentPage === totalPages || isLoadingAccountantView}
              className="custom-pagination-button"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="accountant-view-container">
      <button className="back-button" onClick={handleBack}>
        <i className="fas fa-arrow-left"></i>
        Back to Dashboard
      </button>

      {isLoadingAccountantView ? (
        <div className="loading-spinner">
          <TailSpin color="#0066ff" height={50} width={50} />
        </div>
      ) : (
        <>
          <div className="client-header">
            <h3 className="client-name">
              {accountantData?.clientName || ""}
              <span className="client-email">
                {accountantData?.clientEmail
                  ? `(${accountantData.clientEmail})`
                  : ""}
              </span>
            </h3>
            {/*      {accountantData?.spreadsheetId && (
              
                href={`https://docs.google.com/spreadsheets/d/${accountantData.spreadsheetId}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                className="spreadsheet-link"
              >
                <i className="fas fa-exclamation-triangle spreadsheet-icon"></i>
                Live expenses spreadsheet and user settings
              </a>
            )} */}
          </div>
          {renderReports()}
          {renderNotesModal()}
        </>
      )}
      <VideoHelpModal
        open={showVideoHelp}
        onClose={() => setShowVideoHelp(false)}
        message="Reviewing a Report: Overview"
        videoUrl="https://app.supademo.com/embed/cm96153nj3ys9eyp0zwld5vx0?embed_v=2"
      />
      {showExpensePolicyTutorial && (
        <VideoHelpModal
          open={showExpensePolicyTutorial}
          onClose={() => setShowExpensePolicyTutorial(false)}
          message="Expense Policy Tutorial"
          videoUrl="https://app.supademo.com/embed/cm79jgz4l0kjs11ono5aqsqj6?embed_v=2"
        />
      )}

      {showComplianceDocs && (
        <ComplianceDocumentsModal
          onClose={() => setShowComplianceDocs(false)}
          userEmail={currentUserEmail}
        />
      )}
      {/* Add this right before the closing div in accountantview.js */}
      {showApprovalModal && pendingApproval && (
        <ApprovalConfirmationModal
          onClose={() => setShowApprovalModal(false)}
          onConfirm={handleApprovalConfirmed}
          reportTitle={pendingApproval.title}
          isLoading={isApprovalLoading} // Pass the loading state here
        />
      )}
      {showCommentsPanel && (
        <div
          className="modal-backdrop"
          onClick={() => setShowCommentsPanel(null)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <CommentsSummary
              reportId={showCommentsPanel}
              report={reports.find((r) => r.reportId === showCommentsPanel)}
              onClose={() => setShowCommentsPanel(null)}
              clientEmail={clientEmail} // Add this prop
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountantView;
